// Talent community (`#talent-community`, DS §4 B3 row; BRD §3.5, §9): "Recruiter owns sourcing. Search prior
// finalists, alternates, silver medalists, internal talent and designated talent community members." Plain filters
// and a sort, no ranking; each row is what candidate.search lets this seat see — a rediscovered member outside the
// reader's scope carries the Candidate ID, name and membership facts with the prior requisition as loop facts, and is
// opened and linked by the caller's own search ref (`#candidates/ref/<ref>`), so "rediscovery reuses Candidate ID."
// Archived profiles are included with the Archived pill. No campaign, outreach or enrol control: "do not auto-enroll in
// campaigns". The HR Business Partner reads the values only (Link to requisition is absent).
// His five sets are one inline row of chips; the five filters of his membership fields (Segment · Tags · Reason/date
// added · Prior requisition · Last contact) are the search's own filters and show as chips in the bar; the results sit
// on a flush card, and an empty search reads his sentence instead of a standing banner (UAT D-22, C-8). Segment is a
// taxonomy the Admin publishes (CR-066): with none published the filter is drawn dashed with his sentence. Current
// employer follows the name, as the search row carries it: only where the reader holds the parsed profile in full. A
// member the search sends masked without a name (the Coordinator's read of a member outside their seat) is drawn as
// Candidates draws it, the Candidate ID alone: no name, no profile link, nothing to act on (UAT F-20). A member without a
// segment, while the Admin has published none, reads the same dashed field and sentence as the Segment filter, never a
// bare dash (UAT F-98); a published segment reads its configured word.
// Hosted by MissionShell: views/b3/register.ts registers it through S12's registerView, so it draws no McRoot of its own.

import { useEffect, useState, type ReactNode } from "react";
import type { CandidateSearchInput, SearchRow } from "@/api/tools";
import { candidateSearch } from "@/api/tools";
import { go, useRoute } from "@/router";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import {
  Banner, Button, Card, Chip, Chips, COMMON_LABELS, DIALOG_LABELS, EmptyState, ErrorStrip, Field, FilterBar, Flex, G6_RULE_LABELS, Grid,
  IdPill, Input, MaskedRow, NAV_ITEM_LABELS, PageHeader, Pill, RULE_LABELS, Select, Stack, Table, Td, TextLink, Th, Tr,
  type FilterBarFilter,
} from "@/mc";
import { queryOf, useSettledRead, withQuery } from "@/views/shared/settledRead";
import { BRD } from "./brd";
import { CANDIDATE_LABELS as L, PROFILE_LABELS as P, PROFILE_RULES } from "./labels";
import {
  profileHref, REASONS_ADDED, reasonWord, requisitionWord, searchOf, SETS, STATUS_WORD, TAG, tagSlug, type SetKey,
} from "./profile";
import { LinkDialog, TaxonomySelect, useTaxonomy, type Taxonomy } from "./ProfileDialogs";
import { refusalSentence } from "./refusals";

export function TalentCommunityPage() {
  return <CommunityBody />;
}

const COLUMNS = 9;

/** The five membership filters as typed: a segment slug, tags (comma separated), a reason slug, a requisition id and
 *  the day of the last contact. Empty is no filter. */
export interface CommunityFilterValues {
  segment: string;
  tags: string;
  reason: string;
  prior: string;
  lastContact: string;
}

export const NO_FILTERS: CommunityFilterValues = { segment: "", tags: "", reason: "", prior: "", lastContact: "" };

type Reason = NonNullable<CandidateSearchInput["reason_added"]>[number];

/** The search the sets, the words typed and the five filters ask, or null when no member can match: a reason filter
 *  outside every chosen set. Last contact keeps the members contacted on or after that day. */
export function communitySearch(on: ReadonlySet<SetKey>, q: string, filters: CommunityFilterValues): CandidateSearchInput | null {
  const input = searchOf(on, q);
  const reasons = input.reason_added ?? [];
  if (filters.reason) {
    if (reasons.length > 0 && !reasons.includes(filters.reason as Reason)) return null;
    input.reason_added = [filters.reason as Reason];
  }
  const tags = filters.tags.split(",").map(tagSlug).filter((tag) => TAG.test(tag));
  const prior = filters.prior.trim().toUpperCase();
  if (filters.segment) input.segment = filters.segment;
  if (tags.length > 0) input.tags = tags;
  if (prior) input.prior_requisition_id = prior;
  if (/^\d{4}-\d{2}-\d{2}$/.test(filters.lastContact)) input.last_contact_after = `${filters.lastContact}T00:00:00Z`;
  return input;
}

function CommunityBody() {
  const route = useRoute();
  const { can, me } = useSession();
  const { register } = useViewState();
  const flags = queryOf(route.raw);
  const [q, setQ] = useState("");
  const [on, setOn] = useState<ReadonlySet<SetKey>>(new Set());
  const [filters, setFilters] = useState<CommunityFilterValues>(NO_FILTERS);
  const segment = useTaxonomy("segment");
  const input = communitySearch(on, q, filters);
  const list = useSettledRead(["candidate-search", "community", input], () => candidateSearch(input ?? {}), { enabled: input !== null });
  const rows = input ? (list.data?.rows ?? []) : [];
  const loading = input !== null && list.isLoading;
  useEffect(() => register({ view: "talent-community", ids: {}, summary: { loading, count: rows.length } }), [register, loading, rows.length]);
  const mayLink = can("candidate.link_to_requisition");
  const linking = mayLink && flags.get("action") === "link" && (flags.get("ref") || flags.get("token"));
  const toggle = (key: SetKey, value: boolean) => setOn((prev) => {
    const next = new Set(prev);
    if (value) next.add(key);
    else next.delete(key);
    return next;
  });
  const failed = input !== null ? list.error : null;
  const refused = failed ? refusalSentence(failed) : null;
  return (
    <CommunityView
      q={q}
      onQ={setQ}
      on={on}
      onToggle={toggle}
      filters={filters}
      onFilters={setFilters}
      segment={segment}
      rows={rows}
      loading={loading}
      ready={input === null || Boolean(list.data)}
      link={(row) => mayLink && row.status !== "Merged" && row.projection !== "token"
        ? () => go(withQuery("#talent-community", row.candidate_id ? { action: "link", token: row.candidate_id, ref: null } : { action: "link", ref: row.ref, token: null }, flags))
        : null}
      notice={(
        <>
          {refused ? <Banner tone="red" live>{refused}</Banner> : null}
          {failed && !refused ? (
            <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: failed.status, refusal_code: failed.code, request_id: failed.requestId }} onRetry={() => void list.refetch()}  />
          ) : null}
        </>
      )}
      dialog={linking ? (
        <LinkDialog
          subject={{ token: flags.get("token"), ref: flags.get("token") ? null : flags.get("ref") }}
          roles={me?.roles ?? []}
          onClose={() => go(withQuery("#talent-community", { action: null, ref: null, token: null }, flags))}
          onLinked={(out) => go(out.candidate_id ? profileHref({ token: out.candidate_id }, "applications") : "#talent-community")}
        />
      ) : null}
    />
  );
}

/** The page as drawn from its state: the search, his five sets, the five filters and the results on a flush card. */
export function CommunityView({ q, onQ, on, onToggle, filters, onFilters, segment, rows, loading, ready, link, notice, dialog }: {
  q: string;
  onQ: (q: string) => void;
  on: ReadonlySet<SetKey>;
  onToggle: (key: SetKey, value: boolean) => void;
  filters: CommunityFilterValues;
  onFilters: (filters: CommunityFilterValues) => void;
  segment: Taxonomy;
  rows: readonly SearchRow[];
  loading: boolean;
  /** The search has answered (or can match no one): an empty list is the empty state. */
  ready: boolean;
  link: (row: SearchRow) => (() => void) | null;
  notice: ReactNode;
  dialog: ReactNode;
}) {
  const set = <K extends keyof CommunityFilterValues>(key: K, value: string) => onFilters({ ...filters, [key]: value });
  const segmentWord = (slug: string) => segment.choices.find((choice) => choice.value === slug)?.label ?? slug;
  const chosen: (FilterBarFilter | null)[] = [
    filters.segment ? { name: P.segment, value: segmentWord(filters.segment), onRemove: () => set("segment", ""), hs: "tc.filter.segment.clear" } : null,
    filters.tags.trim() ? { name: P.tags, value: filters.tags.trim(), onRemove: () => set("tags", ""), hs: "tc.filter.tags.clear" } : null,
    filters.reason ? { name: P.reasonDateAdded, value: reasonWord(filters.reason), onRemove: () => set("reason", ""), hs: "tc.filter.reason.clear" } : null,
    filters.prior.trim() ? { name: P.priorRequisition, value: filters.prior.trim().toUpperCase(), onRemove: () => set("prior", ""), hs: "tc.filter.prior.clear" } : null,
    filters.lastContact ? { name: P.lastContact, value: filters.lastContact, onRemove: () => set("lastContact", ""), hs: "tc.filter.last-contact.clear" } : null,
  ];
  const active = chosen.filter((filter): filter is FilterBarFilter => filter !== null);
  return (
    <Stack gap={16}>
      <PageHeader title={NAV_ITEM_LABELS.talentCommunity} />
      <FilterBar label={NAV_ITEM_LABELS.talentCommunity} search={{ value: q, onChange: onQ, hs: "tc.search" }} filters={active} />
      <Chips>
        {SETS.map((item) => (
          <Chip key={item.key} on={on.has(item.key)} multi onToggle={(value) => onToggle(item.key, value)} hs={`tc.set.${item.key.replace(/_/g, "-")}`}>
            {item.label}
          </Chip>
        ))}
      </Chips>
      <Grid cols={5} min={150} gap={12}>
        <TaxonomySelect label={P.segment} taxonomy={segment} value={filters.segment} onChange={(value) => set("segment", value)} sm  />
        <Field label={P.tags}>
          <Input sm value={filters.tags} onChange={(event) => set("tags", event.target.value)} autoComplete="off"  />
        </Field>
        <Field label={P.reasonDateAdded}>
          <Select sm options={REASONS_ADDED} value={filters.reason} onChange={(value) => set("reason", value)}  />
        </Field>
        <Field label={P.priorRequisition}>
          <Input sm mono value={filters.prior} onChange={(event) => set("prior", event.target.value)} autoComplete="off"  />
        </Field>
        <Field label={P.lastContact}>
          <Input sm type="date" value={filters.lastContact} onChange={(event) => set("lastContact", event.target.value)}  />
        </Field>
      </Grid>
      {notice}
      <Card flush>
        <Table
          dense
          stickyFirst
          label={NAV_ITEM_LABELS.talentCommunity}
          loading={loading ? 5 : undefined}
          empty={!loading && ready && rows.length === 0 ? (
            <EmptyState icon="pools" sub={PROFILE_RULES.intentionallyAdds}>{P.designatedMembers}</EmptyState>
          ) : undefined}
          cols={COLUMNS}
          head={(
            <Tr>
              <Th>{COMMON_LABELS.candidateId}</Th>
              <Th>{L.name}</Th>
              <Th>{BRD.currentEmployer}</Th>
              <Th>{P.segment}</Th>
              <Th>{P.tags}</Th>
              <Th>{P.reasonDateAdded}</Th>
              <Th>{P.lastContact}</Th>
              <Th>{P.priorRequisition}</Th>
              <Th>{L.owner}</Th>
            </Tr>
          )}
        >
          {rows.map((row, index) => <Row key={row.ref} row={row} index={index} link={link(row)} segment={segment} />)}
        </Table>
      </Card>
      {dialog}
    </Stack>
  );
}

/** A member's segment: its configured word; with none recorded while the Admin has published none (CR-066), the dashed
 *  field and his sentence the Segment filter draws (D-22: never omitted, never a bare dash). */
function SegmentCell({ value, segment, index }: { value: string | null | undefined; segment: Taxonomy; index: number }) {
  if (value) return <>{segment.choices.find((choice) => choice.value === value)?.label ?? value}</>;
  if (segment.loading || segment.choices.length > 0) return <>—</>;
  return <Select sm aria-label={P.segment} options={[]} value="" blocked={{ reason: G6_RULE_LABELS.adminConfigures }}  />;
}

function Row({ row, index, link, segment }: { row: SearchRow; index: number; link: (() => void) | null; segment: Taxonomy }) {
  if (row.projection === "masked" && !row.display_name) return <MaskedRow cols={COLUMNS} lead={[<span className="mc-mono" key="id">{row.candidate_no}</span>]} />;
  const member = row.community;
  const name = row.display_name ?? "";
  const href = row.candidate_id ? profileHref({ token: row.candidate_id }) : row.projection === "masked" ? profileHref({ ref: row.ref }) : null;
  const prior = member?.prior_requisition;
  return (
    <Tr >
      <Td label={COMMON_LABELS.candidateId} nowrap>
        <Flex gap={6} align="center">
          <IdPill value={row.candidate_no}  />
          {row.status !== "Active" ? <Pill tone="neutral">{STATUS_WORD[row.status]}</Pill> : null}
        </Flex>
      </Td>
      <Td label={L.name}>{href && name ? <TextLink href={href} >{name}</TextLink> : name || "—"}</Td>
      <Td label={BRD.currentEmployer}>{row.current_employer || "—"}</Td>
      <Td label={P.segment}>{member ? <SegmentCell value={member.segment} segment={segment} index={index} /> : "—"}</Td>
      <Td label={P.tags}>{(member?.tags ?? []).join(", ") || "—"}</Td>
      <Td label={P.reasonDateAdded}>{member ? `${reasonWord(member.reason_added)} · ${member.added_at.slice(0, 10)}` : "—"}</Td>
      <Td label={P.lastContact}>{member?.last_contact_at ? member.last_contact_at.slice(0, 10) : "—"}</Td>
      <Td label={P.priorRequisition}>{prior ? `${prior.requisition_id} · ${prior.title} · ${requisitionWord(prior.state)}` : "—"}</Td>
      <Td label={L.owner}>
        <Flex gap={8} align="center" justify="between">
          <span>{row.owner_name ?? "—"}</span>
          {link ? <Button tone="ghost" sm onClick={link} >{DIALOG_LABELS.linkToRequisition}</Button> : null}
        </Flex>
      </Td>
    </Tr>
  );
}
